import React from "react";
import { FeatureGroup, useLeaflet } from "react-leaflet";
import L from "leaflet";
import { EditControl } from "react-leaflet-draw";
import firebase from "../firebase.js";
import { getIconByCategory } from "../helpers.js";
import { checkCommentSafety } from "../utils/profanityCheck.js";
import { SURVEY_OPEN, NEW_COMMENT_TYPE } from "../config.js";

import "./Draw.css";

const Draw = (props) => {
    const { map } = useLeaflet();
    const featureGroupRef = React.useRef(null);

    // Phase 2: Only dreams are allowed
    const dreamIcon = getIconByCategory("general", "dream");

    L.DrawToolbar.include({
        getModeHandlers: function (map) {
            // Only one marker type for Phase 2: dreams
            return [{
                enabled: true,
                handler: new L.Draw.Marker(map, { 
                    icon: dreamIcon,
                }),
                title: "Add a Dream - Share something you'd love to see happen in Herndon's future",
            }];
        },
    });

    L.drawLocal.draw.toolbar.buttons.marker = "Add a Dream";
    L.drawLocal.draw.handlers.marker.tooltip.start =
        "Click map to place your dream marker";

    const handleCreate = (e) => {
        const layer = e.layer;
        
        // Apply the dream icon to the newly created marker
        layer.setIcon(dreamIcon);
        
        map.panTo(layer.getLatLng());
        
        console.log("=== HANDLECREATE FIRED (Phase 2: Dreams Only) ===");
        console.log("New marker created at:", layer.getLatLng());
        
        // Phase 2: All new comments are "dreams_2" type
        const currentType = NEW_COMMENT_TYPE; // "dreams_2"
        layer._pinType = currentType;
        console.log("Type set to:", layer._pinType);
        
        // Disable draw toolbar to prevent clicks on existing markers from creating new ones
        if (map.drawControl) {
            console.log("Disabling draw toolbar");
            map.drawControl.disable();
        }

        var popup = L.popup({ closeButton: true, maxWidth: 400, offset: [0, -20] }).setLatLng(layer.getLatLng()).setContent(
            `<div class="newComment">
                    <h3 class="heading">New Dream</h3>
                    <textarea id="commentText" cols="75" rows="5" placeholder="Share your dream for Herndon..."></textarea>

                    <div class="popCategory">
                        <label>Category</label>
                        <select id="categorySelect">
                            <option value="">Select Category...</option>
                            <option value="play">Play & Recreation</option>
                            <option value="economy">Economy & Jobs</option>
                            <option value="environment">Environment</option>
                            <option value="housing">Housing</option>
                            <option value="infrastructure">Infrastructure</option>
                            <option value="mobility">Mobility & Transit</option>
                        </select>
                    </div>

                    <div class="popReason">
                        <label>What's your relationship to Herndon?</label>
                        <div class='checkGroup'>
                            <input id='popLive' type='checkbox' />
                            <label>I live here</label>
                        </div>
                        <div class='checkGroup'>
                            <input id='popWork' type='checkbox' />
                            <label>I work here</label>
                        </div>
                        <div class='checkGroup'>
                            <input id='popVisit' type='checkbox' />
                            <label>I visit</label>
                        </div>
                    </div>

                    <div class="popName">
                        <label>Optional: Your Name</label>
                        <input id='commentName' type='text'/>
                    </div>

                    <div class="popEmail">
                        <label>Optional: Your Email</label>
                        <input id='commentEmail' type='email' placeholder='Your email'/>
                    </div>
                    
                    <div class="popButtons">
                        <button class="drawSaveBtn button--primary">Post</button>
                        <button class="drawCancelBtn button--secondary">Cancel</button>
                    </div>
                </div>`
        );

        layer.bindPopup(popup).openPopup();

        // Track if the form was successfully saved
        let formWasSaved = false;

        // Re-enable draw and remove layer when popup is closed (by X button, escape, or other means)
        layer.on("popupclose", () => {
            console.log("Popup closed. Was saved:", formWasSaved);
            if (!formWasSaved) {
                console.log("Popup was not saved, removing temporary layer from map");
                map.removeLayer(layer);
            }
            // Re-enable draw toolbar
            if (map.drawControl) {
                console.log("Re-enabling draw toolbar after popup close");
                map.drawControl.enable();
            }
        });

        // Attach event listeners using setTimeout to allow DOM to render
        setTimeout(() => {
            console.log("=== Popup Initialization ===");
            
            // Try both methods to get popup container
            const popupFromContainer = popup._container;
            const popupFromDOM = document.querySelector(".leaflet-popup-content");
            
            console.log("popup._container exists:", !!popupFromContainer);
            console.log("querySelector .leaflet-popup-content exists:", !!popupFromDOM);
            
            const popupPane = popupFromContainer || popupFromDOM;
            
            if (popupPane) {
                console.log("Using popup container, HTML length:", popupPane.innerHTML.length);
                console.log("First 300 chars:", popupPane.innerHTML.substring(0, 300));
            }
            
            if (!popupPane) {
                console.error("Popup container not found via either method!");
                return;
            }

            const categorySelect = popupPane.querySelector("#categorySelect");
            const saveBtn = popupPane.querySelector(".drawSaveBtn");
            const cancelBtn = popupPane.querySelector(".drawCancelBtn");
            const commentTextEl = popupPane.querySelector("#commentText");
            
            console.log("=== Form Elements ===");
            console.log("categorySelect found:", !!categorySelect);
            console.log("saveBtn found:", !!saveBtn);
            console.log("cancelBtn found:", !!cancelBtn);
            console.log("commentText found:", !!commentTextEl);
            
            if (categorySelect) {
                console.log("categorySelect.id:", categorySelect.id);
                console.log("categorySelect.name:", categorySelect.name);
                console.log("categorySelect options:", Array.from(categorySelect.options).map(o => ({ value: o.value, text: o.text })));
                console.log("categorySelect default value:", categorySelect.value);
            }

            // CRITICAL: Stop all click events from propagating to the map
            // This prevents clicking the popup from creating another marker
            const newCommentDiv = popupPane.querySelector(".newComment");
            if (newCommentDiv) {
                newCommentDiv.addEventListener("click", (e) => {
                    e.stopPropagation();
                    console.log("Stopped propagation on click inside .newComment");
                });
            }

            // Update icon dynamically as user changes category selections
            const updateMarkerIcon = () => {
                const category = (categorySelect && categorySelect.value) ? categorySelect.value : "general";
                const storedType = layer._pinType || "dream";
                
                console.log("Icon preview update - category:", category, "type:", storedType);
                
                const newIcon = getIconByCategory(category, storedType);
                layer.setIcon(newIcon);
            };

            // Attach change listeners
            if (categorySelect) {
                categorySelect.addEventListener("change", updateMarkerIcon);
            }

            // Save button handler - direct click listener (more reliable than delegation)
            if (saveBtn) {
                console.log("Attaching DIRECT click listener to save button");
                saveBtn.addEventListener("click", handleSaveClick);
            } else {
                console.error("CRITICAL: saveBtn not found! Cannot attach save handler!");
            }

            function handleSaveClick(e) {
                console.log("\n=== SAVE BUTTON CLICKED ===");
                e.preventDefault();
                e.stopPropagation();
                
                const commentTextEl = popupPane.querySelector("#commentText");
                const commentText = commentTextEl && commentTextEl.value ? commentTextEl.value : "";
                
                const categorySelectCurrent = popupPane.querySelector("#categorySelect");
                console.log("categorySelectCurrent found:", !!categorySelectCurrent, "value:", categorySelectCurrent?.value);
                const category = (categorySelectCurrent && categorySelectCurrent.value) ? categorySelectCurrent.value : "general";
                
                // Use the type stored on the layer when it was created
                const finalType = layer._pinType || NEW_COMMENT_TYPE; // "dreams_2"

                if (!commentText.trim()) {
                    alert("Please write a comment");
                    return;
                }

                const latlng = layer.getLatLng();
                const uid = Date.now().toString();

                const nameEl = popupPane.querySelector("#commentName");
                const emailEl = popupPane.querySelector("#commentEmail");
                const liveEl = popupPane.querySelector("#popLive");
                const workEl = popupPane.querySelector("#popWork");
                const visitEl = popupPane.querySelector("#popVisit");

                const userData = {
                    comment: commentText,
                    commentBy: (nameEl && nameEl.value) ? nameEl.value : "Anonymous",
                    email: (emailEl && emailEl.value) ? emailEl.value : "",
                    submittedOn: new Date().toISOString(),
                    featured: false,
                    hidden: false,
                    live: (liveEl && liveEl.checked) ? liveEl.checked : false,
                    work: (workEl && workEl.checked) ? workEl.checked : false,
                    visit: (visitEl && visitEl.checked) ? visitEl.checked : false,
                    type: finalType,
                    category: category,
                };

                // Check for profanity/inappropriate content
                const safetyCheck = checkCommentSafety(commentText);
                if (safetyCheck.isFlagged) {
                    userData.flagged = true;
                    userData.flagReason = safetyCheck.reason;
                    alert(`⚠️ Comment flagged for: ${safetyCheck.reason}`);
                } else {
                    userData.flagged = false;
                }

                const pointData = {
                    lat: latlng.lat,
                    lng: latlng.lng,
                    type: finalType,
                    category: category,
                };

                console.log("About to save to Firebase:");
                console.log("  uid:", uid);
                console.log("  pointData OBJECT:", pointData);
                console.log("  pointData.category:", pointData.category);
                console.log("  pointData.type:", pointData.type);
                console.log("  pointData.lat:", pointData.lat);
                console.log("  pointData.lng:", pointData.lng);
                console.log("  userData OBJECT:", userData);

                var updates = {};
                updates["/points/" + uid] = pointData;
                updates["/comments/" + uid] = userData;
                
                console.log("Updates object structure:");
                Object.keys(updates).forEach((key) => {
                    console.log(`  "${key}":`, JSON.stringify(updates[key]));
                });
                
                console.log("Calling firebase.database().ref().update()...");
                firebase.database().ref().update(updates).then(() => {
                    console.log(`✓ Firebase update SUCCESSFUL for uid ${uid}`);
                    console.log("  Category was:", category);
                    console.log("  Type was:", finalType);
                    console.log("Removing temporary marker from map - real marker will load from Firebase");
                    formWasSaved = true;
                    layer.closePopup();
                    // Remove the temporary marker - the real one will appear when Firebase syncs
                    map.removeLayer(layer);
                    // Notify parent that comment was created
                    if (props.onCommentCreated) {
                        props.onCommentCreated(uid);
                    }
                }).catch((err) => {
                    console.error(`✗ Firebase update FAILED for uid ${uid}:`, err);
                    // Don't mark as saved on error
                });
            }

            // Cancel button handler
            if (cancelBtn) {
                cancelBtn.addEventListener("click", () => {
                    console.log("Cancel button clicked, closing popup");
                    // Just close the popup - the popupclose handler will remove the layer since formWasSaved is false
                    layer.closePopup();
                });
            }
        }, 150);
    };

    if (!SURVEY_OPEN) {
        // Survey is closed - don't render the drawing tools
        return <FeatureGroup />;
    }

    return (
        <FeatureGroup>
            <EditControl
                position="topleft"
                onCreated={handleCreate}
                draw={{
                    polyline: false,
                    polygon: false,
                    circle: false,
                    rectangle: false,
                    circlemarker: false,
                }}
                edit={{}}
            />
        </FeatureGroup>
    );
};

export default Draw;
